import { useState } from 'react';
import { regions, genres, years, ratings } from '@/mocks/dramas';

interface FilterSidebarProps {
  activeFilters: {
    region: string;
    genre: string;
    year: string;
    rating: string;
  };
  onFilterChange: (key: string, value: string) => void;
  mobileOpen: boolean;
  onMobileClose: () => void;
}

function FilterGroup({
  title,
  icon,
  options,
  active,
  onSelect,
}: {
  title: string;
  icon: string;
  options: string[];
  active: string;
  onSelect: (v: string) => void;
}) {
  const [expanded, setExpanded] = useState(true);

  return (
    <div className="border-b border-background-200 last:border-0">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between py-3 text-sm font-bold text-foreground-900 cursor-pointer"
      >
        <span className="flex items-center gap-2">
          <i className={`${icon} text-primary-500`}></i>
          {title}
        </span>
        <i className={`ri-arrow-down-s-line text-foreground-400 transition-transform duration-200 ${expanded ? '' : '-rotate-90'}`}></i>
      </button>
      {expanded && (
        <div className="flex flex-wrap gap-2 pb-4">
          {options.map((opt) => (
            <button
              key={opt}
              onClick={() => onSelect(opt)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all duration-200 cursor-pointer ${
                active === opt
                  ? 'bg-primary-500 text-background-50'
                  : 'bg-background-100 text-foreground-600 hover:bg-background-200'
              }`}
            >
              {opt}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function FilterSidebar({ activeFilters, onFilterChange, mobileOpen, onMobileClose }: FilterSidebarProps) {
  const sidebarContent = (
    <>
      <h2 className="text-lg font-heading font-bold text-foreground-900 mb-4">
        筛选条件
      </h2>

      <FilterGroup
        title="按地区"
        icon="ri-map-pin-line"
        options={regions}
        active={activeFilters.region}
        onSelect={(v) => onFilterChange('region', v)}
      />

      <FilterGroup
        title="按类型"
        icon="ri-movie-line"
        options={genres}
        active={activeFilters.genre}
        onSelect={(v) => onFilterChange('genre', v)}
      />

      <FilterGroup
        title="按年代"
        icon="ri-calendar-line"
        options={years}
        active={activeFilters.year}
        onSelect={(v) => onFilterChange('year', v)}
      />

      <FilterGroup
        title="按评分"
        icon="ri-star-line"
        options={ratings}
        active={activeFilters.rating}
        onSelect={(v) => onFilterChange('rating', v)}
      />
    </>
  );

  return (
    <>
      <div className="hidden md:block w-[220px] flex-shrink-0">
        <div className="fixed top-[72px] left-0 bottom-0 w-[220px] bg-background-50 border-r border-background-200 overflow-y-auto px-4 py-6 z-40">
          {sidebarContent}
        </div>
      </div>

      {mobileOpen && (
        <>
          <div
            className="fixed inset-0 bg-black/30 z-40 md:hidden"
            onClick={onMobileClose}
          />
          <div className="fixed top-[72px] left-0 bottom-0 w-[260px] bg-background-50 border-r border-background-200 overflow-y-auto px-4 py-6 z-50 md:hidden">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-heading font-bold text-foreground-900">筛选条件</h2>
              <button onClick={onMobileClose} className="w-8 h-8 flex items-center justify-center text-foreground-400 cursor-pointer">
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>
            <FilterGroup
              title="按地区"
              icon="ri-map-pin-line"
              options={regions}
              active={activeFilters.region}
              onSelect={(v) => {
                onFilterChange('region', v);
              }}
            />
            <FilterGroup
              title="按类型"
              icon="ri-movie-line"
              options={genres}
              active={activeFilters.genre}
              onSelect={(v) => {
                onFilterChange('genre', v);
              }}
            />
            <FilterGroup
              title="按年代"
              icon="ri-calendar-line"
              options={years}
              active={activeFilters.year}
              onSelect={(v) => {
                onFilterChange('year', v);
              }}
            />
            <FilterGroup
              title="按评分"
              icon="ri-star-line"
              options={ratings}
              active={activeFilters.rating}
              onSelect={(v) => {
                onFilterChange('rating', v);
              }}
            />
          </div>
        </>
      )}
    </>
  );
}